import { useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import { Eye, EyeOff, Mail, Lock, Info, Check, X } from 'lucide-react';
import { WizardStepLayout } from './WizardStepLayout';

interface SchoolStepPasswordProps {
  contactEmail: string;
  existingPassword?: string;
  onNext: (password: string) => void;
  onBack: () => void;
  onReturnToReview?: () => void;
}

interface StrengthResult {
  score: number;
  label: 'Weak' | 'Fair' | 'Good' | 'Strong';
  barColor: string;
  textColor: string;
  checks: {
    minLength: boolean;
    hasUppercase: boolean;
    hasNumber: boolean;
    hasSpecial: boolean;
  };
}

function getPasswordStrength(password: string): StrengthResult {
  const checks = {
    minLength: password.length >= 8,
    hasUppercase: /[A-Z]/.test(password),
    hasNumber: /[0-9]/.test(password),
    hasSpecial: /[!@#$%^&*()\-_=+[\]{};':"\\|,.<>/?`~]/.test(password),
  };

  const score = [checks.minLength, checks.hasUppercase, checks.hasNumber, checks.hasSpecial].filter(Boolean).length;

  let label: StrengthResult['label'];
  let barColor: string;
  let textColor: string;

  if (score <= 1) {
    label = 'Weak';
    barColor = 'bg-destructive';
    textColor = 'text-destructive';
  } else if (score === 2) {
    label = 'Fair';
    barColor = 'bg-warning';
    textColor = 'text-warning-text';
  } else if (score === 3) {
    label = 'Good';
    barColor = 'bg-primary';
    textColor = 'text-primary';
  } else {
    label = 'Strong';
    barColor = 'bg-success';
    textColor = 'text-success';
  }

  return { score, label, barColor, textColor, checks };
}

function getValidationErrors(password: string, confirmPassword: string): string[] {
  const errors: string[] = [];
  if (password.length < 8) {
    errors.push('Password must be at least 8 characters.');
  }
  if (password.length >= 8 && confirmPassword.length > 0 && password !== confirmPassword) {
    errors.push('Passwords do not match.');
  }
  if (password.length >= 8 && confirmPassword.length === 0) {
    errors.push('Please confirm your password.');
  }
  return errors;
}

export function SchoolStepPassword({ contactEmail, existingPassword, onNext, onBack, onReturnToReview }: SchoolStepPasswordProps) {
  const [password, setPassword] = useState(existingPassword ?? '');
  const [confirmPassword, setConfirmPassword] = useState(existingPassword ?? '');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const strength = useMemo(() => getPasswordStrength(password), [password]);
  const validationErrors = useMemo(
    () => getValidationErrors(password, confirmPassword),
    [password, confirmPassword]
  );

  const handleNext = () => {
    if (validationErrors.length === 0) {
      onNext(password);
    }
  };

  const requirementItems = [
    { met: strength.checks.minLength, label: 'At least 8 characters' },
    { met: strength.checks.hasUppercase, label: 'One uppercase letter' },
    { met: strength.checks.hasNumber, label: 'One number' },
    { met: strength.checks.hasSpecial, label: 'One special character' },
  ];

  return (
    <WizardStepLayout
      currentStep={3}
      totalSteps={8}
      title="Set Your Password"
      subtitle="Choose a secure password for your school admin account"
      onNext={handleNext}
      onBack={onBack}
      showBack={true}
      onReturnToReview={onReturnToReview}
      validationErrors={submitted ? validationErrors : []}
      onSubmitAttempt={() => setSubmitted(true)}
      nextLabel="Continue"
    >
      <div className="space-y-6">

        {/* Read-only email display */}
        <div className="space-y-2">
          <label htmlFor="school-step-password-email" className="block text-sm font-semibold text-foreground">
            Admin Account Email
          </label>
          <div className="relative">
            <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
            <input
              id="school-step-password-email"
              type="email"
              value={contactEmail}
              readOnly
              tabIndex={-1}
              autoComplete="username"
              className="input-premium pl-11 bg-muted/40 text-muted-foreground cursor-default select-all"
            />
          </div>
          <p className="text-xs text-muted-foreground">This is the email you'll use to log in to your school dashboard.</p>
        </div>

        {/* Password field */}
        <div className="space-y-2">
          <label htmlFor="school-step-password-new" className="block text-sm font-semibold text-foreground">
            Password <span className="text-destructive">*</span>
          </label>
          <div className="relative">
            <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
            <input
              id="school-step-password-new"
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => { setPassword(e.target.value); setSubmitted(false); }}
              className="input-premium pl-11 pr-11"
              placeholder="Enter a password"
              autoComplete="new-password"
            />
            <button
              type="button"
              onClick={() => setShowPassword((v) => !v)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors p-1"
              tabIndex={-1}
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>

          {/* Strength indicator */}
          {password.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: -4 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.2 }}
              className="space-y-2 pt-1"
            >
              {/* Bar */}
              <div className="space-y-1">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-muted-foreground">Password strength</span>
                  <span className={`text-xs font-semibold ${strength.textColor}`}>{strength.label}</span>
                </div>
                <div className="flex gap-1">
                  {[1, 2, 3, 4].map((segment) => (
                    <div
                      key={segment}
                      className={`h-1.5 flex-1 rounded-full transition-colors duration-300 ${
                        strength.score >= segment ? strength.barColor : 'bg-muted'
                      }`}
                    />
                  ))}
                </div>
              </div>

              {/* Requirements checklist */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-1">
                {requirementItems.map((req) => (
                  <div key={req.label} className="flex items-center gap-1.5 text-xs">
                    {req.met ? (
                      <Check size={13} className="text-success flex-shrink-0" />
                    ) : (
                      <X size={13} className="text-muted-foreground flex-shrink-0" />
                    )}
                    <span className={req.met ? 'text-success dark:text-green-400' : 'text-muted-foreground'}>
                      {req.label}
                    </span>
                  </div>
                ))}
              </div>
            </motion.div>
          )}
        </div>

        {/* Confirm password field */}
        <div className="space-y-2">
          <label htmlFor="school-step-password-confirm" className="block text-sm font-semibold text-foreground">
            Confirm Password <span className="text-destructive">*</span>
          </label>
          <div className="relative">
            <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
            <input
              id="school-step-password-confirm"
              type={showConfirm ? 'text' : 'password'}
              value={confirmPassword}
              onChange={(e) => { setConfirmPassword(e.target.value); setSubmitted(false); }}
              className="input-premium pl-11 pr-11"
              placeholder="Re-enter your password"
              autoComplete="new-password"
            />
            <button
              type="button"
              onClick={() => setShowConfirm((v) => !v)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors p-1"
              tabIndex={-1}
              aria-label={showConfirm ? 'Hide confirm password' : 'Show confirm password'}
            >
              {showConfirm ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>

          {/* Live match indicator */}
          {confirmPassword.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: -4 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.2 }}
              className={`flex items-center gap-1.5 text-sm ${
                password === confirmPassword ? 'text-success dark:text-green-400' : 'text-destructive'
              }`}
            >
              {password === confirmPassword ? (
                <>
                  <Check size={15} />
                  <span>Passwords match</span>
                </>
              ) : (
                <>
                  <X size={15} />
                  <span>Passwords do not match</span>
                </>
              )}
            </motion.div>
          )}
        </div>

        {/* Info callout */}
        <div className="flex items-start gap-3 p-4 rounded-xl bg-primary/5 border border-primary/10">
          <Info className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
          <p className="text-sm text-muted-foreground">
            You'll use these credentials every time you log back into your school dashboard.
          </p>
        </div>

      </div>
    </WizardStepLayout>
  );
}
